import React from 'react';
import { format as formatTz, utcToZonedTime } from 'date-fns-tz';
import { useTheme } from 'styled-components/macro';

import { getParsedPaymentType, getPaymentType } from '@helpers/dashboard/orders/ordersUtils';
import { EBadgeColors } from '@domain/enums/components/EBadge';
import { ETextWeight } from '@domain/enums/components/EText';
import { numberFormatter, currency as getCurrency } from '@helpers/masks';
import { IOrderTableDataProps } from '@domain/interfaces/dashboard/Orders/IOrderTableData';

import Table from '@components/common/core/Utils/Table';

import * as S from './styles';

const NetRevenue: React.FC<IOrderTableDataProps> = ({ order }) => {
  const theme = useTheme();

  const paymentType = getPaymentType(order.type, theme);
  const parsedPaymentType = getParsedPaymentType(order.type);

  const revenueCurrency = getCurrency(order.currency);
  const formattedGatewayAmount = numberFormatter(order.gateway_amount, 2);

  const revenueValue = `${revenueCurrency} ${formattedGatewayAmount}`;

  const hasCurrencyMetadata =
    order?.currency_metadata && order?.currency_metadata?.original_currency !== 'BRL';
  const currencyRate = order?.currency_metadata?.rate_to_brl;
  const currencyDate = order?.currency_metadata?.conversion_date;
  const currencyOrigin = order?.currency_metadata?.original_currency;
  const totalAmountOriginal = order?.currency_metadata?.original_total_amount;

  const dateFormat = 'dd/MM/yyyy';

  const formattedCurrencyDate = currencyDate
    ? formatTz(utcToZonedTime(new Date(currencyDate), 'UTC'), dateFormat, { timeZone: 'UTC' })
    : undefined;
  const formattedRate = currencyRate ? numberFormatter(currencyRate, 3) : 0;
  const formattedTotalAmount = totalAmountOriginal ? numberFormatter(totalAmountOriginal, 2) : 0;

  return (
    <Table.Data>
      {hasCurrencyMetadata ? (
        <S.Tooltip key={order?.id}>
          <S.Tooltip.Trigger>
            <S.RevenueWrapper>
              <S.RevenueValueNeutro weight={ETextWeight.MEDIUM}>
                {revenueValue}
              </S.RevenueValueNeutro>

              <S.RevenueType>
                <S.RevenueTypeBadge color={EBadgeColors.GRAY}>
                  {paymentType}
                  <S.RevenueTypeText weight={ETextWeight.BOLD}>
                    {parsedPaymentType}
                  </S.RevenueTypeText>
                </S.RevenueTypeBadge>
              </S.RevenueType>
            </S.RevenueWrapper>
          </S.Tooltip.Trigger>
          <S.Tooltip.Content side="top">
            <S.TooltipWrapper>
              <S.TextWrapper>
                <S.DescriptionText>
                  {`Convertido de ${currencyOrigin} ${formattedTotalAmount} em ${formattedCurrencyDate}: 1 BRL = ${formattedRate} ${currencyOrigin}`}
                </S.DescriptionText>
              </S.TextWrapper>
            </S.TooltipWrapper>
          </S.Tooltip.Content>
        </S.Tooltip>
      ) : (
        <S.RevenueWrapper>
          <S.RevenueValueNeutro weight={ETextWeight.MEDIUM}>{revenueValue}</S.RevenueValueNeutro>

          <S.RevenueType>
            <S.RevenueTypeBadge color={EBadgeColors.GRAY}>
              {paymentType}
              <S.RevenueTypeText weight={ETextWeight.BOLD}>{parsedPaymentType}</S.RevenueTypeText>
            </S.RevenueTypeBadge>
          </S.RevenueType>
        </S.RevenueWrapper>
      )}
    </Table.Data>
  );
};

export default NetRevenue;
